import { createElement, useState } from "react";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { Trans } from "react-i18next";
import { useTranslation } from "@localization/useTranslation";
import { isNullOrWhitespace } from "@common/stringOperations";
import { post, put } from "@common/api/apiShared";
import { Result } from "@common/api/result";
import { useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";
import { useValidator } from "@common/hooks/validation";
import { getEnumValues } from "@common/enumOperations";
import { IRiskObservationDialog } from "./RiskObservationDialog";
import { RiskObservationSaveModel, RiskObservationSource } from "./RiskObservationDialog.types";

function createEmptyModel(): RiskObservationSaveModel {
    return { id: "", name: "", description: "", source: RiskObservationSource.Manual, observedBy: "", observedDate: new Date() };
}

export function useRiskObservationDialogDataMapping(props: IRiskObservationDialog) {
    const snackbar = useDotLegalSnackbar();
    const { translateString, translateRiskObservationSource } = useTranslation();

    const [viewModel, setViewModel] = useState<RiskObservationSaveModel>(props.selectedObservation ?? createEmptyModel());

    const validation = useValidator<{ name: string; observedBy?: string; observedDate: Date | null }>((validators) => ({
        name: validators.validateNotEmpty((x) => x.name, translateString("observation")),
        observedBy: validators.validateNotEmpty((x) => x.observedBy, translateString("observedBy")),
        observedDate: validators.validateNotEmpty((x) => x.observedDate, translateString("observedDate")),
    }));

    validation.check({ name: viewModel.name, observedBy: viewModel.observedBy, observedDate: viewModel.observedDate });

    const sourceOptions = getEnumValues(RiskObservationSource).map((source) => ({ id: source.toString(), name: translateRiskObservationSource(source) }));

    const saveMutation = useDotLegalMutation(save, {
        onSuccess: (response: Result<string>) => {
            if (response.success()) {
                props.onSave();
                props.onCloseDialog();
                snackbar.show(createElement(Trans, { i18nKey: isEdit() ? "dataUpdated" : "dataCreated", values: { name: viewModel.name } }));
            }
        },
    });

    function save() {
        const body = {
            name: viewModel.name,
            description: viewModel.description,
            source: viewModel.source,
            observedBy: viewModel.observedBy,
            observedDate: viewModel.observedDate,
        };
        return isEdit() ? put<string>(`/observations/${viewModel.id}`, body) : post<string>("/observations", body);
    }

    const setName = (name: string) => {
        setViewModel({ ...viewModel, name });
    };

    const setDescription = (description: string) => {
        setViewModel({ ...viewModel, description });
    };

    const setSource = (source: string | null) => {
        setViewModel({ ...viewModel, source: source ? (Number(source) as RiskObservationSource) : RiskObservationSource.Manual });
    };

    const setObservedBy = (observedBy: string) => {
        setViewModel({ ...viewModel, observedBy });
    };

    const setObservedDate = (observedDate: Date | null) => {
        setViewModel({ ...viewModel, observedDate });
    };

    const validateAndSave = () => {
        if (validation.anyHasErrors) {
            validation.setShowErrors(true);
            return;
        }

        saveMutation.mutateAsync();
    };

    function isEdit() {
        return !isNullOrWhitespace(viewModel.id);
    }

    return {
        viewModel,
        validation,
        inProgress: saveMutation.isLoading,
        isEdit,
        setName,
        setDescription,
        setSource,
        setObservedBy,
        setObservedDate,
        sourceOptions,
        validateAndSave,
    };
}
